import React, { useCallback, useState } from 'react';
import { Button, Field, Input, MessageBar, MessageBarBody, Select, Spinner, Text } from '@fluentui/react-components';
import { ArrowRightRegular, CheckmarkCircleRegular, CopyRegular, OpenRegular, ShieldLockRegular } from '@fluentui/react-icons';
import { connectionsService } from '../../api/connectionsService';
import { ADMIN_CONSENT_STATE_STORAGE_KEY, createAdminConsentState } from '../../utils/adminConsentState';

interface Props { onComplete: (connectionId: string) => void; }
interface ApplicationIdentity { clientId: string; applicationName: string; }
type Verification = { intent: 'success' | 'error'; message: string } | null;

const GUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

const ApplicationInsightsSetup: React.FC<Props> = ({ onComplete }) => {
  const [tenantId, setTenantId] = useState('');
  const [subscriptionId, setSubscriptionId] = useState('');
  const [resourceGroupName, setResourceGroupName] = useState('');
  const [applicationInsightsName, setApplicationInsightsName] = useState('');
  const [lookbackDays, setLookbackDays] = useState('7');
  const [profile, setProfile] = useState<'auto' | 'classic' | 'workspace'>('auto');
  const [consentGranted, setConsentGranted] = useState(false);
  const [identity, setIdentity] = useState<ApplicationIdentity | null>(null);
  const [checkingConsent, setCheckingConsent] = useState(false);
  const [openingConsent, setOpeningConsent] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [saving, setSaving] = useState(false);
  const [verification, setVerification] = useState<Verification>(null);
  const [error, setError] = useState<string | null>(null);

  const validTenant = GUID.test(tenantId.trim());
  const validResource = validTenant && GUID.test(subscriptionId.trim())
    && !!resourceGroupName.trim() && !!applicationInsightsName.trim()
    && Number.isInteger(Number(lookbackDays)) && Number(lookbackDays) >= 1 && Number(lookbackDays) <= 30;
  const busy = checkingConsent || openingConsent || verifying || saving;

  const resetConsent = (value: string) => {
    setTenantId(value);
    setConsentGranted(false);
    setIdentity(null);
    setVerification(null);
    setError(null);
  };

  const rememberIdentity = (value: { client_id: string; application_name: string }) => {
    setIdentity({ clientId: value.client_id, applicationName: value.application_name });
  };

  const checkConsent = useCallback(async () => {
    if (!validTenant) return;
    setCheckingConsent(true);
    setError(null);
    setVerification(null);
    try {
      const result = await connectionsService.getAdminConsentStatus(tenantId.trim());
      rememberIdentity(result);
      setConsentGranted(result.granted);
      if (!result.granted) setError(result.message);
    } catch (reason) {
      setConsentGranted(false);
      setError(reason instanceof Error ? reason.message : 'Tenant consent could not be checked');
    } finally {
      setCheckingConsent(false);
    }
  }, [tenantId, validTenant]);

  const beginConsent = useCallback(async () => {
    if (!validTenant) return;
    const state = createAdminConsentState();
    sessionStorage.setItem(ADMIN_CONSENT_STATE_STORAGE_KEY, state);
    const consentWindow = window.open('', '_blank');
    if (!consentWindow) {
      sessionStorage.removeItem(ADMIN_CONSENT_STATE_STORAGE_KEY);
      setError('The consent window was blocked. Allow pop-ups for this site and try again.');
      return;
    }
    consentWindow.opener = null;
    setOpeningConsent(true);
    setError(null);
    setVerification(null);
    try {
      const consent = await connectionsService.initiateAdminConsent(tenantId.trim(), state);
      rememberIdentity(consent);
      consentWindow.location.href = consent.authorization_url;
    } catch (reason) {
      consentWindow.close();
      sessionStorage.removeItem(ADMIN_CONSENT_STATE_STORAGE_KEY);
      setError(reason instanceof Error ? reason.message : 'Microsoft admin consent could not be opened');
    } finally {
      setOpeningConsent(false);
    }
  }, [tenantId, validTenant]);

  const config = () => ({
    tenantId: tenantId.trim(), subscriptionId: subscriptionId.trim(),
    resourceGroupName: resourceGroupName.trim(), applicationInsightsName: applicationInsightsName.trim(),
    lookbackDays: Number(lookbackDays),
    profile,
  });

  const verifyAccess = useCallback(async () => {
    if (!consentGranted || !validResource) return;
    setVerifying(true);
    setError(null);
    setVerification(null);
    try {
      const result = await connectionsService.verifyApplicationInsightsAccess(config());
      setVerification({ intent: result.success ? 'success' : 'error', message: result.message });
    } catch (reason) {
      setVerification({
        intent: 'error',
        message: reason instanceof Error ? reason.message : 'Application Insights access could not be verified',
      });
    } finally {
      setVerifying(false);
    }
  }, [tenantId, subscriptionId, resourceGroupName, applicationInsightsName, lookbackDays, profile, consentGranted, validResource]);

  const saveConnection = useCallback(async () => {
    if (verification?.intent !== 'success') return;
    setSaving(true);
    setError(null);
    try {
      const created = await connectionsService.createConnection({
        connectorId: 'application_insights',
        name: `${applicationInsightsName.trim()} (Application Insights)`,
        metadataConfig: config(),
      });
      onComplete(created.id);
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : 'The verified connection could not be saved');
    } finally {
      setSaving(false);
    }
  }, [tenantId, subscriptionId, resourceGroupName, applicationInsightsName, lookbackDays, profile, verification, onComplete]);

  const copyApplicationName = async () => {
    if (!identity) return;
    try { await navigator.clipboard.writeText(identity.applicationName); }
    catch { setError('The application name could not be copied. Select and copy it manually.'); }
  };

  return (
    <div className="application-insights-setup">
      <header className="application-insights-header">
        <ShieldLockRegular fontSize={40} />
        <div>
          <Text size={500} weight="semibold">Connect Azure Application Insights</Text>
          <Text block>Grant tenant consent, assign read-only resource access, then verify the connection.</Text>
        </div>
      </header>

      <section className="application-insights-stage" aria-labelledby="application-insights-consent-heading">
        <Text id="application-insights-consent-heading" size={400} weight="semibold">1. Grant tenant consent</Text>
        <Text block>A Microsoft Entra administrator for the customer tenant must grant consent to the ClaraBI import application.</Text>
        <Field label="Customer tenant ID" hint="Microsoft Entra tenant (directory) ID." required>
          <Input value={tenantId} onChange={(_, data) => resetConsent(data.value)} disabled={busy} />
        </Field>
        <div className="application-insights-button-row">
          <Button icon={openingConsent ? <Spinner size="tiny" /> : <OpenRegular />} disabled={!validTenant || busy} onClick={beginConsent}>
            {openingConsent ? 'Opening…' : 'Open admin consent'}
          </Button>
          <Button appearance="primary" icon={checkingConsent ? <Spinner size="tiny" /> : <CheckmarkCircleRegular />}
            disabled={!validTenant || busy} onClick={checkConsent}>
            {checkingConsent ? 'Checking…' : 'Check consent'}
          </Button>
        </div>
        {consentGranted && <MessageBar intent="success"><MessageBarBody>Tenant consent is available.</MessageBarBody></MessageBar>}
      </section>

      <section className="application-insights-stage" aria-labelledby="application-insights-rbac-heading">
        <Text id="application-insights-rbac-heading" size={400} weight="semibold">2. Assign resource access</Text>
        <Text block>
          In the Azure portal, open the target Application Insights resource, select <strong>Access control (IAM)</strong>,
          and assign <strong>Monitoring Reader</strong>. Search for the enterprise application by the application name below.
        </Text>
        {identity ? (
          <div className="application-insights-identity">
            <Text size={200}>Application</Text>
            <div className="application-insights-client-id">
              <Text weight="semibold">{identity.applicationName}</Text>
              <Button appearance="subtle" size="small" icon={<CopyRegular />} aria-label="Copy application name" onClick={copyApplicationName} />
            </div>
            <Text size={200}>Client ID (verify only)</Text>
            <code>{identity.clientId}</code>
          </div>
        ) : <Text size={200}>Enter a valid tenant ID and check consent to display the application identity.</Text>}
        <ul className="application-insights-guidance">
          <li>Scope the assignment to the Application Insights resource, not the subscription.</li>
          <li>For Auto or Workspace mode, also assign <strong>Log Analytics Reader</strong> on the linked workspace so empty and custom tables can be enumerated.</li>
          <li>Use the client ID only to verify that the selected enterprise application is correct.</li>
        </ul>
      </section>

      <section className="application-insights-stage" aria-labelledby="application-insights-resource-heading">
        <Text id="application-insights-resource-heading" size={400} weight="semibold">3. Configure and verify</Text>
        <Text block>Resource fields become available after tenant consent is confirmed.</Text>
        <Field label="Azure subscription ID" required>
          <Input value={subscriptionId} onChange={(_, data) => { setSubscriptionId(data.value); setVerification(null); }} disabled={!consentGranted || busy} />
        </Field>
        <Field label="Resource group" required>
          <Input value={resourceGroupName} onChange={(_, data) => { setResourceGroupName(data.value); setVerification(null); }} disabled={!consentGranted || busy} />
        </Field>
        <Field label="Application Insights resource name" required>
          <Input value={applicationInsightsName} onChange={(_, data) => { setApplicationInsightsName(data.value); setVerification(null); }} disabled={!consentGranted || busy} />
        </Field>
        <Field label="Lookback (days)" hint="Full snapshots are limited to 1–30 days." required>
          <Input type="number" min={1} max={30} value={lookbackDays}
            onChange={(_, data) => { setLookbackDays(data.value); setVerification(null); }} disabled={!consentGranted || busy} />
        </Field>
        <Field label="Telemetry profile" hint="Auto detects workspace-based resources. Classic only discovers tables containing rows.">
          <Select value={profile} onChange={(_, data) => { setProfile(data.value as typeof profile); setVerification(null); }}
            disabled={!consentGranted || busy}>
            <option value="auto">Auto (recommended)</option>
            <option value="workspace">Workspace</option>
            <option value="classic">Classic</option>
          </Select>
        </Field>
        <Button appearance="primary" icon={verifying ? <Spinner size="tiny" /> : <CheckmarkCircleRegular />}
          disabled={!consentGranted || !validResource || busy} onClick={verifyAccess}>
          {verifying ? 'Verifying access…' : 'Verify access'}
        </Button>
        {verification && <MessageBar intent={verification.intent}><MessageBarBody>{verification.message}</MessageBarBody></MessageBar>}
      </section>

      {error && <MessageBar intent="error"><MessageBarBody>{error}</MessageBarBody></MessageBar>}
      <div className="application-insights-actions">
        <Button appearance="primary" icon={saving ? <Spinner size="tiny" /> : <ArrowRightRegular />} iconPosition="after"
          disabled={verification?.intent !== 'success' || busy} onClick={saveConnection}>
          {saving ? 'Saving…' : 'Save and select telemetry'}
        </Button>
      </div>
    </div>
  );
};

export default ApplicationInsightsSetup;